import { memo, useState, useRef, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { UploadedImage, ReviewStatus } from '../../services/uploadConnector';
import {
  MoreHorizontal,
  Download,
  Trash2,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Eye,
  FileImage,
  Check,
  Wand2,
  ArrowLeftRight,
  Undo2,
  ClipboardCheck,
  XCircle,
  RotateCcw,
  Sparkles,
  ImageIcon,
  Eraser,
  Maximize,
  Replace,
  Palette,
  Shirt,
  Home,
  AlertTriangle,
  X,
} from 'lucide-react';
import { format } from 'date-fns';

interface AssetCardProps {
  asset: UploadedImage;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onDownload?: (asset: UploadedImage) => void;
  onDelete?: (asset: UploadedImage) => void;
  onView?: (asset: UploadedImage) => void;
  onTransform?: (asset: UploadedImage) => void;
  onCompare?: (asset: UploadedImage) => void;
  onRevert?: (asset: UploadedImage) => void;
  onSubmitForReview?: (asset: UploadedImage) => void;
}

// Processing status configuration
const PROCESSING_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  completed: { label: 'Processed', color: 'text-emerald-600', bgColor: 'bg-emerald-500/10', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  processing: { label: 'Processing', color: 'text-blue-600', bgColor: 'bg-blue-500/10', icon: <Loader2 className="w-3.5 h-3.5 animate-spin" /> },
  failed: { label: 'Failed', color: 'text-red-600', bgColor: 'bg-red-500/10', icon: <AlertCircle className="w-3.5 h-3.5" /> },
  uploaded: { label: 'Pending', color: 'text-amber-600', bgColor: 'bg-amber-500/10', icon: <Clock className="w-3.5 h-3.5" /> },
};

// Review status configuration
const REVIEW_STATUS_CONFIG: Record<ReviewStatus, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  none: { label: '', color: '', bgColor: '', icon: null },
  pending: { label: 'Pending Review', color: 'text-amber-600', bgColor: 'bg-amber-500/10', icon: <Clock className="w-3.5 h-3.5" /> },
  in_review: { label: 'In Review', color: 'text-blue-600', bgColor: 'bg-blue-500/10', icon: <Eye className="w-3.5 h-3.5" /> },
  approved: { label: 'Approved', color: 'text-emerald-600', bgColor: 'bg-emerald-500/10', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  rejected: { label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-500/10', icon: <XCircle className="w-3.5 h-3.5" /> },
  revision_requested: { label: 'Revision', color: 'text-orange-600', bgColor: 'bg-orange-500/10', icon: <RotateCcw className="w-3.5 h-3.5" /> },
};

// Transformation type icons and labels
const TRANSFORMATION_CONFIG: Record<string, { icon: React.ReactNode; label: string; shortLabel: string }> = {
  background_removal: { icon: <Eraser className="w-3 h-3" />, label: 'Background Removal', shortLabel: 'BG Remove' },
  upscale: { icon: <Maximize className="w-3 h-3" />, label: 'AI Upscale', shortLabel: 'Upscale' },
  generative_fill: { icon: <Sparkles className="w-3 h-3" />, label: 'Generative Fill', shortLabel: 'Gen Fill' },
  generative_remove: { icon: <Eraser className="w-3 h-3" />, label: 'Generative Remove', shortLabel: 'Gen Remove' },
  generative_replace: { icon: <Replace className="w-3 h-3" />, label: 'Generative Replace', shortLabel: 'Gen Replace' },
  generative_recolor: { icon: <Palette className="w-3 h-3" />, label: 'Generative Recolor', shortLabel: 'Recolor' },
  invisible_mannequin: { icon: <Shirt className="w-3 h-3" />, label: 'Invisible Mannequin', shortLabel: 'Ghost' },
  virtual_staging: { icon: <Home className="w-3 h-3" />, label: 'Virtual Staging', shortLabel: 'Staging' },
  format_conversion: { icon: <ImageIcon className="w-3 h-3" />, label: 'Format Conversion', shortLabel: 'Convert' },
  gemini_studio_retouch: { icon: <Sparkles className="w-3 h-3" />, label: 'Studio Retouch', shortLabel: 'Retouch' },
  studio_retouch: { icon: <Sparkles className="w-3 h-3" />, label: 'Studio Retouch', shortLabel: 'Retouch' },
  resize: { icon: <Maximize className="w-3 h-3" />, label: 'Resize', shortLabel: 'Resize' },
  quality: { icon: <Sparkles className="w-3 h-3" />, label: 'Quality Enhancement', shortLabel: 'Quality' },
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get transformation display info
const getTransformationInfo = (type: string) => {
  // Handle variations like "quality enhancement", "quality en"
  const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
  if (normalizedType.startsWith('quality')) {
    return TRANSFORMATION_CONFIG.quality;
  }
  return TRANSFORMATION_CONFIG[type] || TRANSFORMATION_CONFIG[normalizedType] || {
    icon: <Wand2 className="w-3 h-3" />,
    label: type.replace(/_/g, ' '),
    shortLabel: type.replace(/_/g, ' ').slice(0, 12)
  };
};

export const AssetCard = memo(function AssetCard({
  asset,
  isSelected = false,
  onSelect,
  onDownload,
  onDelete,
  onView,
  onTransform,
  onCompare,
  onRevert,
  onSubmitForReview,
}: AssetCardProps) {
  const [showRevisionNotes, setShowRevisionNotes] = useState(false);
  const revisionNotesRef = useRef<HTMLDivElement>(null);

  // Close revision notes when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (revisionNotesRef.current && !revisionNotesRef.current.contains(event.target as Node)) {
        setShowRevisionNotes(false);
      }
    };

    if (showRevisionNotes) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRevisionNotes]);

  const thumbnailUrl = asset.processed_url || asset.thumbnail_url || asset.original_url;
  const fileSize = asset.file_size_bytes && asset.file_size_bytes > 0 ? formatFileSize(asset.file_size_bytes) : null;
  const dimensions = asset.width && asset.height && asset.width > 0 && asset.height > 0 ? `${asset.width}×${asset.height}` : null;

  const reviewStatus = asset.review_status || 'none';
  const hasTransformations = asset.applied_transformations && asset.applied_transformations.length > 0;
  const isProcessed = asset.status === 'completed' && asset.processed_url;
  // Allow submission for 'none' (new images) and 'revision_requested' (re-submission after changes)
  const canSubmitForReview = isProcessed && (reviewStatus === 'none' || reviewStatus === 'revision_requested');

  const processingConfig = PROCESSING_STATUS_CONFIG[asset.status] || PROCESSING_STATUS_CONFIG.uploaded;
  const reviewConfig = REVIEW_STATUS_CONFIG[reviewStatus];

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(asset.id, !isSelected);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
    if (placeholder) placeholder.style.display = 'flex';
  };

  return (
    <TooltipProvider>
      <Card
        className={`
          group relative overflow-hidden transition-all duration-200
          hover:shadow-xl hover:shadow-black/10
          ${isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}
        `}
      >
        {/* Image Container - 4:3 Aspect Ratio */}
        <div
          className="relative aspect-[4/3] bg-muted cursor-pointer overflow-hidden"
          onClick={() => onView?.(asset)}
        >
          {/* Selection Checkbox - Always visible */}
          {onSelect && (
            <button
              onClick={handleSelectClick}
              className={`
                absolute top-3 left-3 z-20
                w-6 h-6 rounded-md
                flex items-center justify-center
                transition-all duration-150
                ${isSelected
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-black/40 backdrop-blur-sm text-white hover:bg-black/60'
                }
              `}
            >
              {isSelected ? (
                <Check className="w-4 h-4" strokeWidth={3} />
              ) : (
                <div className="w-3 h-3 rounded-sm border-2 border-current" />
              )}
            </button>
          )}

          {/* Revision Warning Icon */}
          {asset.review_notes && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRevisionNotes(!showRevisionNotes);
                }}
                className="absolute top-3 right-3 z-20 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-lg hover:bg-orange-600 transition-colors animate-pulse hover:animate-none"
              >
                <AlertTriangle className="w-4 h-4" />
              </button>
              {showRevisionNotes && (
                <div
                  ref={revisionNotesRef}
                  className="absolute top-12 right-2 z-30 w-56 rounded-lg shadow-xl overflow-hidden border border-orange-500/30"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="bg-orange-500 px-3 py-2 flex items-center justify-between">
                    <span className="text-white text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Revision Notes
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowRevisionNotes(false);
                      }}
                      className="text-white/80 hover:text-white"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-3 bg-card">
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">
                      {asset.review_notes}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Image */}
          <img
            src={thumbnailUrl}
            alt={asset.original_filename}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            onError={handleImageError}
            loading="lazy"
            decoding="async"
          />
          <div className="hidden absolute inset-0 items-center justify-center bg-muted">
            <FileImage className="w-16 h-16 text-muted-foreground/50" />
          </div>

          {/* Processing Overlay */}
          {asset.status === 'processing' && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center text-white">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                <span className="text-sm font-medium">Processing...</span>
              </div>
            </div>
          )}
          {asset.status === 'failed' && (
            <div className="absolute inset-0 bg-red-500/20 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-red-500">Failed</span>
              </div>
            </div>
          )}

          {/* Transformation Badges - Bottom of image */}
          {hasTransformations && (
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
              <div className="flex flex-wrap gap-1">
                {asset.applied_transformations!.slice(0, 4).map((t, i) => {
                  const info = getTransformationInfo(t.type);
                  return (
                    <Tooltip key={i}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] font-medium">
                          {info.icon}
                          {info.shortLabel}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{info.label}</TooltipContent>
                    </Tooltip>
                  );
                })}
                {asset.applied_transformations!.length > 4 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] font-medium">
                    +{asset.applied_transformations!.length - 4}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Quick View Button - Center on hover */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-black/60 backdrop-blur-sm rounded-full p-3 pointer-events-auto">
              <Eye className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="p-3 space-y-2">
          {/* Filename & Status Row */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-medium text-sm truncate flex-1" title={asset.original_filename}>
              {asset.original_filename}
            </h3>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Processing Status Badge */}
              <Badge variant="secondary" className={`${processingConfig.bgColor} ${processingConfig.color} border-0 text-[10px] px-1.5 py-0 h-5 gap-1`}>
                {processingConfig.icon}
                {processingConfig.label}
              </Badge>
              {/* Review Status Badge */}
              {reviewStatus !== 'none' && (
                <Badge variant="secondary" className={`${reviewConfig.bgColor} ${reviewConfig.color} border-0 text-[10px] px-1.5 py-0 h-5 gap-1`}>
                  {reviewConfig.icon}
                  {reviewConfig.label}
                </Badge>
              )}
            </div>
          </div>

          {/* Metadata Row */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {dimensions && <span>{dimensions}</span>}
            {dimensions && fileSize && <span className="text-muted-foreground/30">•</span>}
            {fileSize && <span>{fileSize}</span>}
            {(dimensions || fileSize) && asset.format && <span className="text-muted-foreground/30">•</span>}
            {asset.format && <span className="uppercase">{asset.format}</span>}
            <span className="ml-auto">{format(new Date(asset.created_at), 'MMM d, yyyy')}</span>
          </div>
        </div>

        {/* Action Bar - Always visible */}
        <div className="px-3 pb-3 pt-2 space-y-2 border-t border-border/50">
          {/* Primary Row: Transform & Compare */}
          <div className="flex items-center gap-2">
            {onTransform && asset.status !== 'processing' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="h-9 px-4 gap-2 flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTransform(asset);
                    }}
                  >
                    <Wand2 className="w-4 h-4" />
                    Transform
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Apply transformations</TooltipContent>
              </Tooltip>
            )}

            {onCompare && isProcessed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-4 gap-2 flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompare(asset);
                    }}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                    Compare
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Compare before/after</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Secondary Row: Review, Download, More */}
          <div className="flex items-center gap-2">
            {onSubmitForReview && canSubmitForReview && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className={`h-9 px-4 gap-2 flex-1 text-white ${
                      reviewStatus === 'revision_requested'
                        ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSubmitForReview(asset);
                    }}
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    {reviewStatus === 'revision_requested' ? 'Resubmit' : 'Submit for Review'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {reviewStatus === 'revision_requested' ? 'Resubmit after revision' : 'Submit for Review'}
                </TooltipContent>
              </Tooltip>
            )}

            {onDownload && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(asset);
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download</TooltipContent>
              </Tooltip>
            )}

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 flex-shrink-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onView && (
                  <DropdownMenuItem onClick={() => onView(asset)}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Full Size
                  </DropdownMenuItem>
                )}
                {onRevert && isProcessed && (
                  <DropdownMenuItem onClick={() => onRevert(asset)}>
                    <Undo2 className="w-4 h-4 mr-2" />
                    Revert to Original
                  </DropdownMenuItem>
                )}
                {thumbnailUrl && (
                  <DropdownMenuItem onClick={() => window.open(thumbnailUrl, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(asset)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
});
